import React from "react";
import {
  ArrowRightIcon,
  AwardIcon,
  BriefcaseIcon,
  BuildingIcon,
  CalendarRangeIcon,
  CheckIcon,
  DevicesIcon,
  GraduationIcon,
  LayoutListIcon,
  RocketIcon,
  RouteIcon,
  ShapesIcon,
  SparklesIcon,
  UsersRoundIcon,
  VenusIcon,
} from "./landing/Icons";
import { STATS } from "./landing/content";
import { LeadFormProvider } from "./landing/lead-form/LeadFormProvider";
import SupportTierCards from "./landing/programmes/SupportTierCards";
import { Breadcrumb, CONTAINER, GRID_BACKDROP, PrimaryLink, Section, SectionHeading, SectionLabel, SecondaryLink } from "./landing/ui";

const PRIORITIES = [
  "Leadership continuity",
  "High-potential development",
  "Graduate readiness",
  "Women in leadership",
  "Career and industry exposure",
] as const;

const USE_CASE_GROUPS = [
  {
    title: "For organisations developing their people",
    cases: [
      {
        Icon: RouteIcon,
        name: "Succession planning",
        page: "successionPlanning",
        challenge: "Critical roles have named successors, but readiness and knowledge transfer aren't moving fast enough.",
        outcome: "Get successors ready sooner and pass on critical knowledge before it leaves the organisation.",
      },
      {
        Icon: SparklesIcon,
        name: "High-potential talent",
        challenge: "High-potential employees need broader exposure, clearer direction and support for their next role.",
        outcome: "Move high-potentials into bigger roles sooner and give them stronger reasons to stay.",
      },
      {
        Icon: BriefcaseIcon,
        name: "Graduate and early careers",
        challenge: "New hires are finding their way around an unfamiliar organisation without enough practical guidance.",
        outcome: "Help graduates and early-career employees build confidence, contribute sooner and stay connected.",
      },
      {
        Icon: VenusIcon,
        name: "Women in leadership",
        page: "womenLeadership",
        challenge: "Capable women are doing strong work but aren't consistently moving into visible leadership roles.",
        outcome: "Build confidence, visibility and a stronger pipeline of women ready for broader responsibility.",
      },
    ],
  },
  {
    title: "For foundations, universities and ecosystem partners",
    cases: [
      {
        Icon: RocketIcon,
        name: "Entrepreneurs and startups",
        challenge: "Founders need relevant guidance to make better decisions while they build and grow their businesses.",
        outcome: "Help entrepreneurs grow through structured cohorts, practical goals and experienced industry mentors.",
      },
      {
        Icon: AwardIcon,
        name: "Scholars",
        challenge: "Scholars have academic support but can lack career direction, industry context and professional networks.",
        outcome: "Help scholarship recipients build career confidence, workplace readiness and useful industry connections.",
      },
      {
        Icon: GraduationIcon,
        name: "Universities and career services",
        challenge: "Students need a credible industry view before they make career choices and start work.",
        outcome: "Connect students with industry mentors who make career paths and workplace expectations clearer.",
      },
    ],
  },
] as const;

const CHOICES = [
  { Icon: UsersRoundIcon, term: "Who mentors", detail: `Your own leaders, our ${STATS[1].value} industry mentors, or a blend.` },
  { Icon: ShapesIcon, term: "Format", detail: "One-to-one, group mentoring circles, or both." },
  { Icon: CalendarRangeIcon, term: "Length and rhythm", detail: "From a short pilot cohort to an ongoing programme." },
  { Icon: BuildingIcon, term: "Cohort size", detail: "One team, or the whole organisation." },
  { Icon: LayoutListIcon, term: "Themes and goals", detail: "Built around your competency framework or our tested themes." },
  { Icon: DevicesIcon, term: "Delivery", detail: "Online on the platform, with in-person sessions if you want them." },
] as const;

const JOURNEY = [
  {
    title: "Design the programme",
    body: "Agree the audience, outcomes, journey and success measures around your talent priority.",
  },
  {
    title: "Match mentors and mentees",
    body: "Use programme criteria and participant goals to make relevant matches.",
  },
  {
    title: "Run sessions on the platform",
    body: "Participants get one place for their journey, scheduling, sessions and follow-ups.",
  },
  {
    title: "Measure and report outcomes",
    body: "Track activity, progress and feedback so leadership can see what changed.",
  },
] as const;

type Props = {
  programmePlanUrl: string;
  successionPlanningUrl: string;
  womenLeadershipUrl: string;
  midaGuideUrl: string;
  businessCaseUrl: string;
  pricingUrl: string;
  platformUrl: string;
};

export default function ProgrammesPage({
  programmePlanUrl,
  successionPlanningUrl,
  womenLeadershipUrl,
  midaGuideUrl,
  businessCaseUrl,
  pricingUrl,
  platformUrl,
}: Props) {
  const pageUrls = { successionPlanning: successionPlanningUrl, womenLeadership: womenLeadershipUrl };

  return (
    <LeadFormProvider>
      <section className="tw-relative tw-bg-[#f4f8fc] tw-pb-24 tw-pt-20 sm:tw-pb-14 sm:tw-pt-10">
        <div aria-hidden className="tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-40" style={GRID_BACKDROP} />

        <div
          className={`${CONTAINER} tw-relative tw-grid tw-grid-cols-[1.08fr_.72fr] tw-items-end tw-gap-10 sm:tw-grid-cols-1 sm:tw-gap-8`}
        >
          <div className="tw-min-w-0">
            <Breadcrumb current="Programmes" />
            {/* The approved H1, mirrored in static_pages/programmes.html.erb; keep both in step. */}
            <h1 className="tw-mt-6 tw-mb-0 tw-max-w-4xl tw-text-[clamp(2.75rem,5.1vw,4.5rem)] sm:tw-text-[clamp(2.25rem,8vw,2.75rem)] tw-font-black tw-leading-[1.02] tw-tracking-[-.05em] tw-text-[#10233f]">
              Mentoring programmes for every talent priority, designed around how you work
            </h1>
            <p className="tw-mt-6 tw-mb-0 tw-max-w-3xl tw-text-xl tw-leading-8 tw-text-[#536a82] sm:tw-text-lg xs:tw-text-base xs:tw-leading-7">
              Succession, high-potential talent, graduates, women leaders, entrepreneurs, scholars and
              university students, all on one platform. You choose the mentors, format and timeline, with
              as much or as little design and management support as you need.
            </p>

            <div className="tw-mt-8 tw-flex tw-flex-row tw-gap-3 sm:tw-flex-col">
              <PrimaryLink href={programmePlanUrl} data-lead-form="programme_plan">
                Get a recommended programme plan
                <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
              </PrimaryLink>
              <SecondaryLink href={businessCaseUrl}>Build your business case</SecondaryLink>
            </div>
          </div>

          <div className="tw-min-w-0 tw-rounded-[28px] tw-border tw-border-[#c5dcea] tw-bg-white tw-p-8 tw-shadow-[0_24px_60px_rgba(23,72,111,.12)] xs:tw-rounded-[24px] xs:tw-p-5">
            <SectionLabel>Start with the outcome</SectionLabel>
            <p className="tw-mt-4 tw-mb-0 tw-text-3xl tw-font-black tw-leading-tight tw-tracking-[-.04em] tw-text-[#173754] xs:tw-text-2xl">
              One mentoring platform. Different talent priorities.
            </p>
            <ul className="tw-mt-6 tw-mb-0 tw-list-none tw-space-y-3 tw-p-0">
              {PRIORITIES.map((priority) => (
                <li
                  key={priority}
                  className="tw-flex tw-gap-3 tw-rounded-xl tw-bg-[#f4f8fc] tw-p-3 tw-text-sm tw-font-bold tw-text-[#314b64]"
                >
                  <CheckIcon aria-hidden className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#0f7a62]" />
                  {priority}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Section id="use-cases" className="tw-scroll-mt-24">
        <div className="tw-max-w-3xl">
          <SectionLabel>Start with the challenge</SectionLabel>
          <SectionHeading>Common use cases</SectionHeading>
          <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c] xs:tw-text-base xs:tw-leading-7">
            Pick the talent outcome that matters now. We&rsquo;ll help you shape the journey, mentor
            model and measurement around how your organisation works.
          </p>
        </div>

        <div className="tw-mt-12 tw-space-y-14 sm:tw-mt-9 sm:tw-space-y-11">
          {USE_CASE_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="tw-m-0 tw-text-3xl tw-font-black tw-leading-tight tw-tracking-[-.03em] tw-text-[#173754] xs:tw-text-xl">
                {group.title}
              </h3>

              <div className="tw-mt-6 tw-grid tw-grid-cols-3 tw-gap-5 md:tw-grid-cols-2 sm:tw-mt-5 sm:tw-grid-cols-1 sm:tw-gap-4">
                {group.cases.map(({ Icon, name, challenge, outcome, ...useCase }) => (
                  <article
                    key={name}
                    className="tw-flex tw-min-w-0 tw-flex-col tw-rounded-[26px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-7 tw-shadow-[0_16px_44px_rgba(32,75,110,.07)] xs:tw-rounded-[22px] xs:tw-p-5"
                  >
                    <span
                      aria-hidden
                      className="tw-flex tw-h-16 tw-w-16 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[20px] tw-bg-[#eaf6fd] tw-text-[#087bd3]"
                    >
                      <Icon className="tw-h-8 tw-w-8" />
                    </span>
                    <h4 className="tw-mt-6 tw-mb-0 tw-text-2xl tw-font-black tw-leading-tight tw-tracking-[-.035em] tw-text-[#173754] xs:tw-mt-5 xs:tw-text-[1.4rem]">
                      {name}
                    </h4>
                    <p className="tw-mt-4 tw-mb-0 tw-text-base tw-leading-7 tw-text-[#60768c] xs:tw-mt-3">
                      <strong className="tw-text-[#304d68]">Challenge:</strong> {challenge}
                    </p>
                    <p className="tw-mt-4 tw-mb-6 tw-text-sm tw-leading-6 tw-text-[#60768c] xs:tw-mt-3">
                      <strong className="tw-text-[#304d68]">Outcome:</strong> {outcome}
                    </p>
                    {/* Programmes without their own page open the recommended-plan enquiry instead. */}
                    <a
                      {...("page" in useCase
                        ? { href: pageUrls[useCase.page] }
                        : { href: programmePlanUrl, "data-lead-form": "programme_plan" })}
                      className="tw-mt-auto tw-flex tw-min-h-12 tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-[#dce8f2] tw-pt-5 tw-text-sm tw-font-black tw-leading-5 tw-text-[#0068b8] tw-no-underline hover:tw-text-[#005a9f]"
                    >
                      <span>
                        {"page" in useCase
                          ? `Explore the ${name.toLowerCase()} programme`
                          : `Talk to us about ${name.toLowerCase()}`}
                      </span>
                      <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
                    </a>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="tw-border-y tw-border-[#d7e4ef] tw-bg-[#f4f8fc]">
        <div className="tw-max-w-3xl">
          <SectionLabel>Flexible by design</SectionLabel>
          <SectionHeading>Run it your way</SectionHeading>
          <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c] xs:tw-text-base xs:tw-leading-7">
            You decide how the programme fits your people, systems and internal capacity. We provide the
            platform and the level of support you need.
          </p>
        </div>

        <div className="tw-mt-12 sm:tw-mt-9">
          <h3 className="tw-m-0 tw-text-2xl tw-font-black tw-tracking-[-.03em] tw-text-[#173754]">You choose</h3>
          <div className="tw-mt-6 tw-grid tw-grid-cols-3 tw-gap-4 sm:tw-grid-cols-2 xs:tw-grid-cols-1">
            {CHOICES.map(({ Icon, term, detail }) => (
              <article key={term} className="tw-rounded-[22px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-6 xs:tw-rounded-[20px] xs:tw-p-5">
                <Icon aria-hidden className="tw-h-6 tw-w-6 tw-text-[#087bd3]" />
                <h4 className="tw-mt-4 tw-mb-0 tw-text-lg tw-font-black tw-text-[#173754]">{term}</h4>
                <p className="tw-mt-2 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{detail}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="tw-mt-14 sm:tw-mt-10">
          <h3 className="tw-m-0 tw-text-2xl tw-font-black tw-tracking-[-.03em] tw-text-[#173754]">
            How much support you want
          </h3>
          <div className="tw-mt-6">
            <SupportTierCards heading="h4" />
          </div>

          <div className="tw-mt-8 tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-5 tw-rounded-[22px] tw-bg-[#10233f] tw-p-6 tw-text-white sm:tw-flex-col sm:tw-items-stretch xs:tw-p-5">
            <p className="tw-m-0 tw-text-lg tw-font-black">Not sure which fits? We&rsquo;ll recommend a programme plan.</p>
            <div className="tw-flex tw-flex-row tw-gap-3 xs:tw-flex-col">
              <a
                href={programmePlanUrl}
                data-lead-form="programme_plan"
                className="tw-inline-flex tw-min-h-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#0068b8] tw-px-6 tw-py-3 tw-text-sm tw-font-black tw-text-white tw-no-underline hover:tw-bg-[#005a9f] hover:tw-text-white"
              >
                Get a programme plan
              </a>
              <a
                href={pricingUrl}
                className="tw-inline-flex tw-min-h-12 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-white/40 tw-px-6 tw-py-3 tw-text-sm tw-font-black tw-text-white tw-no-underline hover:tw-bg-white/10 hover:tw-text-white"
              >
                See pricing
              </a>
            </div>
          </div>
        </div>
      </Section>

      <Section className="tw-bg-[#f4f8fc]">
        <div className="tw-grid tw-grid-cols-[.72fr_1.28fr] tw-items-end tw-gap-8 sm:tw-grid-cols-1 sm:tw-gap-5">
          <div>
            <SectionLabel>How every programme works</SectionLabel>
            <SectionHeading>A clear journey from priority to proof.</SectionHeading>
          </div>
          <p className="tw-m-0 tw-max-w-2xl tw-justify-self-end tw-text-lg tw-leading-8 tw-text-[#60768c] sm:tw-justify-self-start xs:tw-text-base xs:tw-leading-7">
            Every programme runs on the same connected workflow. The content, mentor model and measures
            change around your objective.
          </p>
        </div>

        <ol
          aria-label="Four stages of a FutureLab mentoring programme"
          className="tw-mt-12 tw-mb-0 tw-grid tw-list-none tw-grid-cols-4 tw-gap-4 tw-p-0 md:tw-grid-cols-2 sm:tw-mt-9 xs:tw-grid-cols-1"
        >
          {JOURNEY.map((stage, index) => (
            <li key={stage.title} className="tw-rounded-[24px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-6 xs:tw-rounded-[22px] xs:tw-p-5">
              <span
                aria-hidden
                className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#0068b8] tw-text-sm tw-font-black tw-text-white"
              >
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="tw-mt-5 tw-mb-0 tw-text-xl tw-font-black tw-text-[#173754]">{stage.title}</h3>
              <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{stage.body}</p>
            </li>
          ))}
        </ol>

        <a
          href={platformUrl}
          className="tw-mt-8 tw-inline-flex tw-min-h-11 tw-items-center tw-gap-2 tw-font-black tw-text-[#0068b8] tw-no-underline hover:tw-text-[#005a9f]"
        >
          See the mentoring platform
          <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
        </a>
      </Section>

      <section aria-label="FutureLab in numbers" className="tw-bg-[#10233f] tw-py-16 tw-text-white sm:tw-py-12">
        <div className="tw-mx-auto tw-grid tw-max-w-[1000px] tw-grid-cols-3 tw-gap-8 tw-px-8 sm:tw-px-5 xs:tw-grid-cols-1 xs:tw-gap-6">
          {STATS.map((stat) => (
            <div key={stat.label}>
              <p className="tw-m-0 tw-text-4xl tw-font-black tw-tracking-[-.045em] tw-text-[#70d9ce] xs:tw-text-3xl">
                {stat.value}
              </p>
              <p className="tw-mt-2 tw-mb-0 tw-font-bold tw-leading-5 tw-text-[#d9e7f1] xs:tw-text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="tw-py-24 sm:tw-py-14">
        <div className={CONTAINER}>
          <div className="tw-relative tw-grid tw-grid-cols-[1.2fr_.8fr] tw-items-center tw-overflow-hidden tw-rounded-[30px] tw-bg-[#087bd3] tw-p-12 tw-text-white sm:tw-grid-cols-1 sm:tw-p-10 xs:tw-rounded-[24px] xs:tw-p-6">
            <div
              aria-hidden
              className="tw-pointer-events-none tw-absolute -tw-right-20 -tw-top-20 tw-h-72 tw-w-72 tw-rounded-full tw-border-[42px] tw-border-solid tw-border-white/10"
            />
            <div className="tw-relative tw-min-w-0">
              <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-leading-5 tw-tracking-[.15em] tw-text-[#d9efff]">
                Malaysia · Succession planning
              </p>
              <h2 className="tw-mt-4 tw-mb-0 tw-text-4xl tw-font-black tw-leading-tight tw-tracking-[-.04em] xs:tw-text-[1.75rem]">
                Employing expatriates in Malaysia? MIDA&rsquo;s succession plan requirement takes effect
                from 1 January 2027.
              </h2>
              <p className="tw-mt-5 tw-mb-0 tw-max-w-3xl tw-leading-7 tw-text-white">
                The policy is there to make sure expatriates pass their knowledge on to local employees.
                Organisations are encouraged to name suitable successors now and prepare their training
                and knowledge-transfer plans.
              </p>
            </div>
            <div className="tw-relative tw-mt-0 tw-justify-self-end sm:tw-mt-7 sm:tw-justify-self-start xs:tw-w-full">
              <PrimaryLink href={midaGuideUrl} variant="inverse" className="xs:tw-w-full">
                Read the guide
                <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
              </PrimaryLink>
            </div>
          </div>
        </div>
      </section>
    </LeadFormProvider>
  );
}
